/**
 * Creating a trigger — the rule that pays somebody.
 *
 * The conditions are the part worth getting right in the UI. They are
 * "absent means do not care", never "must be zero", and a tier is a FLOOR
 * rather than an equality — a trigger for tier 1 fires for a tier 3 sub, or
 * every streamer needs three triggers. A form that renders `tier: 2000` in a
 * box invites exactly the opposite belief, so each condition is worded.
 */
import { useState, type ReactNode } from 'react';
import {
  TWITCH_TRIGGER_TYPES, type Bundle, type Reward, type TriggerInput,
  type TwitchTriggerType,
} from '@tomo/contracts';
import { Shell } from '../components/Shell.jsx';
import { Button, Failed, Spinner } from '../components/primitives.jsx';
import { Check, Choice, Field, Num, Text } from '../components/form.jsx';
import { bundles as bundlesApi, rewards as rewardsApi, triggers } from '../lib/tomo.js';
import { ApiError } from '../lib/api.js';
import { useAsync } from '../useAsync.js';
import { navigate, hrefFor } from '../lib/router.js';

const TYPE_LABEL: Record<TwitchTriggerType, string> = {
  subscription: 'Someone subscribes',
  resub: 'Someone resubscribes',
  gift_sub: 'Someone gifts subs',
  channel_points: 'Someone redeems channel points',
  bits: 'Someone cheers bits',
  follow: 'Someone follows',
  raid: 'Someone raids the channel',
  chat_command: 'Someone types a command in chat',
};

export function NewTrigger({ channelId }: { channelId: string }): ReactNode {
  const [catalog] = useAsync(
    async (signal) => ({
      rewards: await rewardsApi.list(channelId, signal),
      bundles: await bundlesApi.list(channelId, signal),
    }),
    [channelId]
  );

  if (catalog.state === 'loading') {
    return <Shell channelId={channelId}><Spinner label="Loading the catalog…" /></Shell>;
  }
  if (catalog.state === 'failed') {
    return (
      <Shell channelId={channelId}>
        <Failed message="Could not load the catalog." detail={catalog.error.message} />
      </Shell>
    );
  }
  return <Form channelId={channelId} {...catalog.value} />;
}

function Form(
  { channelId, rewards, bundles }:
  { channelId: string; rewards: Reward[]; bundles: Bundle[] }
): ReactNode {
  const [name, setName] = useState('');
  const [type, setType] = useState<TwitchTriggerType>('chat_command');
  const [command, setCommand] = useState('!tomo');
  const [tier, setTier] = useState<'' | '1000' | '2000' | '3000'>('');
  const [minBits, setMinBits] = useState(0);
  const [minMonths, setMinMonths] = useState(0);
  const [minGifts, setMinGifts] = useState(0);
  const [minRaiders, setMinRaiders] = useState(0);
  const [allowAnon, setAllowAnon] = useState(false);

  const [currencyCode, setCurrencyCode] = useState('');
  const [currencyAmount, setCurrencyAmount] = useState(10);
  const [bundleId, setBundleId] = useState('');
  const [rewardId, setRewardId] = useState('');

  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  // A trigger that pays nothing fires happily and does nothing, which is
  // indistinguishable from a trigger that is broken. The server refuses it;
  // the button says so first.
  const paysSomething =
    (currencyCode.length > 0 && currencyAmount > 0) || bundleId.length > 0 || rewardId.length > 0;
  const commandOk = type !== 'chat_command' || command.trim().length > 0;
  const fields = error?.byField() ?? {};

  const conditions = (): TriggerInput['matches'][number]['conditions'] => ({
    // Absent means DO NOT CARE. A zero here would mean "must be zero", which
    // is how a trigger configured for any tier stopped matching tier 1.
    ...(type === 'chat_command' && command.trim() ? { chat_command: command.trim() } : {}),
    ...(tier ? { tier } : {}),
    ...(minBits > 0 ? { min_bits: minBits } : {}),
    ...(minMonths > 0 ? { min_months: minMonths } : {}),
    ...(minGifts > 0 ? { min_gift_count: minGifts } : {}),
    ...(minRaiders > 0 ? { min_raid_viewers: minRaiders } : {}),
    ...(allowAnon ? { allow_anonymous: true } : {}),
  });

  const submit = (): void => {
    setBusy(true);
    setError(null);
    const input: TriggerInput = {
      name,
      provider: 'twitch',
      matches: [{ type, conditions: conditions() }],
      payouts: {
        currency: currencyCode && currencyAmount > 0
          ? [{ code: currencyCode.trim(), amount: currencyAmount }]
          : [],
        reward_ids: rewardId ? [rewardId] : [],
        bundle_ids: bundleId ? [bundleId] : [],
      },
      enabled,
    };
    triggers.create(channelId, input).then(
      ({ id }) => { navigate(hrefFor([channelId, 'triggers', id])); },
      (err: unknown) => {
        setBusy(false);
        setError(err instanceof ApiError
          ? err
          : new ApiError('malformed_response', 0, 'Something unexpected went wrong.'));
      }
    );
  };

  return (
    <Shell channelId={channelId}>
      <h1 className="text-xl font-semibold tracking-tight">New trigger</h1>
      <p className="mt-1 text-sm text-ink-400">
        Something happens on Twitch, and someone gets paid.
      </p>

      {error ? <div className="mt-6"><Failed message={error.message} /></div> : null}

      <form
        className="mt-8 max-w-lg space-y-6"
        onSubmit={(e) => { e.preventDefault(); submit(); }}
      >
        <Field label="Name" hint="Only you see this." error={fields.name}>
          <Text value={name} onChange={setName} placeholder="Chat opener" maxLength={120} />
        </Field>

        <Field label="Fires when">
          <Choice
            value={type}
            onChange={setType}
            options={TWITCH_TRIGGER_TYPES.map((t) => ({ value: t, label: TYPE_LABEL[t] }))}
          />
        </Field>

        {type === 'chat_command' ? (
          <Field label="The command" hint="Matched case-insensitively.">
            <Text value={command} onChange={setCommand} placeholder="!tomo" maxLength={40} />
          </Field>
        ) : null}

        {(type === 'subscription' || type === 'resub' || type === 'gift_sub') ? (
          <Field
            label="Minimum tier"
            // A FLOOR, not an equality. Said in the hint because the opposite
            // belief is the natural one to have.
            hint="A floor: a tier 1 trigger also fires for tier 2 and tier 3."
          >
            <Choice
              value={tier}
              onChange={setTier}
              options={[
                { value: '', label: 'Any tier' },
                { value: '1000', label: 'Tier 1 and above' },
                { value: '2000', label: 'Tier 2 and above' },
                { value: '3000', label: 'Tier 3 only' },
              ]}
            />
          </Field>
        ) : null}

        {type === 'resub' ? (
          <Field label="Minimum months" hint="0 means any.">
            <Num value={minMonths} onChange={setMinMonths} min={0} />
          </Field>
        ) : null}

        {type === 'gift_sub' ? (
          <>
            <Field label="Minimum gifted at once" hint="0 means any.">
              <Num value={minGifts} onChange={setMinGifts} min={0} />
            </Field>
            <Check
              checked={allowAnon}
              onChange={setAllowAnon}
              // Theirs tracked anonymous gifters and silently paid nobody,
              // which looked like a bug from the outside.
              label="Also fire for anonymous gifters (nobody is paid — there is no viewer to pay)"
            />
          </>
        ) : null}

        {type === 'bits' ? (
          <Field label="Minimum bits" hint="0 means any.">
            <Num value={minBits} onChange={setMinBits} min={0} />
          </Field>
        ) : null}

        {type === 'raid' ? (
          <Field label="Minimum raiders" hint="0 means any.">
            <Num value={minRaiders} onChange={setMinRaiders} min={0} />
          </Field>
        ) : null}

        <fieldset className="space-y-4 rounded-lg border border-ink-800 p-4">
          <legend className="px-1 text-sm text-ink-200">Pays out</legend>

          <div className="flex gap-3">
            <span className="flex-1">
              <Field label="Currency code" hint="Must already exist on this channel.">
                <Text value={currencyCode} onChange={setCurrencyCode} placeholder="sparks" maxLength={20} />
              </Field>
            </span>
            <span className="w-28">
              <Field label="Amount">
                <Num value={currencyAmount} onChange={setCurrencyAmount} min={0} />
              </Field>
            </span>
          </div>

          <Field label="A bundle to open" error={fields['payouts.bundle_ids[0]']}>
            <Choice
              value={bundleId}
              onChange={setBundleId}
              options={[
                { value: '', label: 'None' },
                ...bundles.map((b) => ({ value: b.id, label: b.active ? b.name : `${b.name} (inactive)` })),
              ]}
            />
          </Field>

          <Field label="A reward, granted directly" error={fields['payouts.reward_ids[0]']}>
            <Choice
              value={rewardId}
              onChange={setRewardId}
              options={[
                { value: '', label: 'None' },
                ...rewards.map((r) => ({ value: r.id, label: r.active ? r.name : `${r.name} (inactive)` })),
              ]}
            />
          </Field>

          {!paysSomething ? (
            <p className="text-xs text-warn-400">
              A trigger that pays nothing fires happily and does nothing.
            </p>
          ) : null}
        </fieldset>

        <Check checked={enabled} onChange={setEnabled} label="Enabled" />

        <div className="flex gap-3">
          <Button type="submit" disabled={busy || name.length === 0 || !paysSomething || !commandOk}>
            {busy ? 'Creating…' : 'Create trigger'}
          </Button>
          <Button variant="ghost" onClick={() => { navigate(hrefFor([channelId, 'triggers'])); }}>
            Cancel
          </Button>
        </div>
      </form>
    </Shell>
  );
}
